import { useParams, useNavigate } from 'react-router-dom'
import { useDealByIdentifier } from "@/hooks/useDealByIdentifier"
import { uploadImageToStorageSecure } from "@/lib/secureFileUpload"
import { useAuthState } from "@/hooks/useAuth"
import { useEffect, useRef, useState } from 'react'
import { Button } from "@/components/ui/button"
import { ArrowLeft, Camera, MapPin } from "lucide-react"
import { doc, updateDoc, collection, query, where, getDocs, getDoc } from "firebase/firestore"
import { db } from "@/lib/firebase"
import type { PortfolioItem, Artist } from "@/types/models"
import { FlashCard } from "@/components/shared/tattoo/FlashCard"
import { BaseMasonryGrid } from "@/components/shared/BaseMasonryGrid"
import { BannerUploadOptions, type BannerFormat } from "@/components/shared/BannerUploadOptions"

export default function DealDetailPage() {
  const { identifier } = useParams<{ identifier: string }>()
  const navigate = useNavigate()
  const { user: authUser } = useAuthState()
  const { deal, loading: dealLoading, error: dealError } = useDealByIdentifier(identifier)
  
  // State for flashes
  const [dealFlashes, setDealFlashes] = useState<PortfolioItem[]>([])
  const [flashesLoading, setFlashesLoading] = useState(true)
  const [flashArtists, setFlashArtists] = useState<Map<string, Artist>>(new Map())
  const [dealArtist, setDealArtist] = useState<Artist | null>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const bannerInputRef = useRef<HTMLInputElement | null>(null)

  // Banner upload state
  const [showBannerUploadOptions, setShowBannerUploadOptions] = useState(false)
  const [uploadingBanner, setUploadingBanner] = useState(false)
  const [isDealOwner, setIsDealOwner] = useState(false)
  const [bannerDimensions, setBannerDimensions] = useState({ width: '100%', height: '100%' })

  // Load flashes associated with this deal
  useEffect(() => {
    const loadDealFlashes = async () => {
      if (!deal?.id) {
        setFlashesLoading(false)
        return
      }

      try {
        setFlashesLoading(true)
        
        // Query flashes that have this deal ID or are tagged with it
        // Since deals contain selected flash IDs, we need to find them
        const flashesQuery = query(
          collection(db, 'flashes'),
          where('discount.dealId', '==', deal.id)
        )
        
        const querySnapshot = await getDocs(flashesQuery)
        
        const flashes: PortfolioItem[] = querySnapshot.docs.map(doc => {
          const data = doc.data()
          return {
            id: doc.id,
            artistId: data.artistId,
            type: data.type || 'image',
            storagePath: data.storagePath || '',
            imageUrl: data.imageUrl,
            compressedImageUrl: data.compressedImageUrl,
            width: data.width || 0,
            height: data.height || 0,
            styleTags: data.styles || [],
            caption: data.caption || '',
            createdAt: data.createdAt?.toDate?.() ?? new Date(),
            isFlash: true,
            minPrice: data.minPrice,
            minSize: data.minSize,
            available: data.available,
            booked: data.booked,
            discount: data.discount ? {
              absoluteAmount: data.discount.absoluteAmount,
              procentualAccount: data.discount.procentualAccount,
              newPrice: data.discount.newPrice,
              oldPrice: data.discount.oldPrice,
              discountStartsAt: data.discount.discountStartsAt?.toDate?.() || new Date(data.discount.discountStartsAt),
              discountEndsAt: data.discount.discountEndsAt?.toDate?.() || new Date(data.discount.discountEndsAt)
            } : undefined,
          } satisfies PortfolioItem
        })

        // Fetch artist data for each flash
        const artistsMap = new Map<string, Artist>()
        const artistIds = [...new Set(flashes.map(f => f.artistId))]
        
        for (const artistId of artistIds) {
          try {
            const artistRef = doc(db, 'artists', artistId)
            const artistSnap = await getDoc(artistRef)
            
            if (artistSnap.exists()) {
              const artistData = artistSnap.data() as Artist
              artistsMap.set(artistId, artistData)
            }
          } catch (error) {
            console.error(`Error fetching artist ${artistId}:`, error)
          }
        }

        setDealFlashes(flashes)
        setFlashArtists(artistsMap)
        console.log(`Loaded ${flashes.length} flashes for deal ${deal.id}`)
      } catch (error) {
        console.error('Error loading deal flashes:', error)
      } finally {
        setFlashesLoading(false)
      }
    }

    loadDealFlashes()
  }, [deal?.id])

  // Check if current user is the deal owner
  useEffect(() => {
    // For now, we'll allow any authenticated user to edit
    // This should be updated to check actual ownership
    setIsDealOwner(!!authUser?.uid)
  }, [authUser])

  // Load artist information for the deal
  useEffect(() => {
    const loadDealArtist = async () => {
      if (!deal?.artistId) {
        setDealArtist(null)
        return
      }

      try {
        const artistRef = doc(db, 'artists', deal.artistId)
        const artistSnap = await getDoc(artistRef)
        
        if (artistSnap.exists()) {
          const artistData = artistSnap.data() as Artist
          setDealArtist(artistData)
        }
      } catch (error) {
        console.error(`Error fetching artist ${deal.artistId}:`, error)
      }
    }

    loadDealArtist()
  }, [deal?.artistId])

  // Function to extract dominant color from image
  const extractDominantColor = (image: HTMLImageElement) => {
    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // Set canvas size to a small sample for performance
      canvas.width = 50
      canvas.height = 50

      // Draw the image on canvas
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height)

      // Get image data from top portion (where notch is)
      const imageData = ctx.getImageData(0, 0, canvas.width, 25) // Top half only
      const data = imageData.data

      // Calculate average color from top portion
      let r = 0, g = 0, b = 0, count = 0
      for (let i = 0; i < data.length; i += 4) {
        r += data[i]
        g += data[i + 1]
        b += data[i + 2]
        count++
      }

      if (count > 0) {
        r = Math.round(r / count)
        g = Math.round(g / count)
        b = Math.round(b / count)
        
        // Convert to hex
        const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
        
        // Update theme color
        const themeColorMeta = document.querySelector('meta[name="theme-color"]')
        if (themeColorMeta) {
          themeColorMeta.setAttribute('content', hex)
        }
      }
    } catch (error) {
      // Silently fail if canvas is tainted by cross-origin images
      console.debug('Could not extract dominant color due to cross-origin restrictions')
    }
  }

  // Handle banner image upload
  const handleBannerImageSelect = async (fileChangeEvent: React.ChangeEvent<HTMLInputElement>) => {
    const file = fileChangeEvent.target.files?.[0]
    if (!file || !authUser?.uid || !deal?.id) return
    
    const format = (fileChangeEvent.target as any).dataset.format as BannerFormat
    if (!format || (format !== 'poster' && format !== 'instagram')) {
      alert('Invalid format selected')
      return
    }
    
    setShowBannerUploadOptions(false)
    setUploadingBanner(true)
    
    try {
      const downloadURL = await uploadImageToStorageSecure(
        file,
        `deals/${deal.id}/banner`,
        authUser.uid
      )
      
      // Update the deal in Firestore
      const dealRef = doc(db, 'deals', deal.id)
      await updateDoc(dealRef, {
        dealBannerUrl: downloadURL
      })
      
      console.log('Banner uploaded and deal updated successfully')
    } catch (error) {
      console.error('Banner upload failed:', error)
      alert(`Banner upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setUploadingBanner(false)
    }
  }

  // Reset theme color when component unmounts
  useEffect(() => {
    return () => {
      const themeColorMeta = document.querySelector('meta[name="theme-color"]')
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', '#ffffff')
      }
    }
  }, [])

  if (dealLoading || flashesLoading) {
    return (
      <div className="min-h-screen bg-background" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
        <div className="text-center py-8">
          <p className="text-muted-foreground">Loading deal...</p>
        </div>
      </div>
    )
  }

  if (dealError || !deal) {
    return (
      <div className="min-h-screen bg-background" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
        <div className="text-center py-8">
          <p className="text-muted-foreground">Deal not found</p>
        </div>
      </div>
    )
  }

  // Format dates
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const validFromDate = deal.validFrom
  const validToDate = deal.validTo

  // Calculate banner dimensions with aspect ratio constraints
  const BANNER_HEIGHT_VH = 30
  const calculateBannerDimensions = () => {
    if (!imageRef.current) return { width: '100%', height: '100%' }
    
    const img = imageRef.current
    const aspectRatio = img.naturalWidth / img.naturalHeight
    
    // Cap at 1:1 aspect ratio (square) - prevents images wider than a box
    const constrainedRatio = Math.min(aspectRatio, 1)
    
    // Banner height is 40vh, width is constrained by the ratio but capped at height
    const width = `${Math.round(BANNER_HEIGHT_VH * constrainedRatio)}vh`
    
    return { width, height: '100%' }
  }

  const handleBannerImageLoad = () => {
    if (imageRef.current) {
      setBannerDimensions(calculateBannerDimensions())
      extractDominantColor(imageRef.current)
    }
  }

  return (
    <>
      <div className="min-h-screen bg-background" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
        {/* Top bar with back button and edit button */}
        <div className="px-4 pt-4 pb-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10 rounded-full"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          {isDealOwner && (
            <Button
              onClick={() => navigate(`/deals/${deal.identifier}/edit`)}
              className="px-6"
            >
              Edit
            </Button>
          )}
        </div>

        {/* Banner Container - centered with top padding */}
        <div className="px-4 pb-4 flex justify-center">
          {/* Banner Image - box-constrained with aspect ratio limits */}
          <div 
            className="relative overflow-hidden rounded-lg shadow-md"
            style={{ 
              width: bannerDimensions.width,
              height: `${BANNER_HEIGHT_VH}vh`,
              maxWidth: '100%'
            }}
          >
            <img
              ref={imageRef}
              src={deal.dealBannerUrl}
              alt={deal.dealTitle}
              className="w-full h-full object-cover"
              onLoad={handleBannerImageLoad}
            />
            
            {/* Banner Upload Overlay */}
            {isDealOwner && (
              <div
                onClick={() => setShowBannerUploadOptions(true)}
                className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black/40 opacity-0 cursor-pointer transition-opacity hover:opacity-100"
              >
                <Camera className="w-8 h-8 text-white" />
                <span className="text-sm font-medium text-white">Change Banner</span>
              </div>
            )}
          </div>
        </div>

        {/* Content Section */}
        <div className="phone-container-padded pb-8">
          {/* Deal Title */}
          <div className="pt-6 pb-2">
            <h1 className="text-2xl font-bold text-foreground leading-tight">
              {deal.dealTitle}
            </h1>
          </div>

          {/* Artist Info */}
          {dealArtist && (
            <div className="flex items-center gap-3 pb-6">
              {dealArtist.compressedAvatarUrl || dealArtist.avatarUrl ? (
                <img 
                  src={dealArtist.compressedAvatarUrl || dealArtist.avatarUrl}
                  alt={dealArtist.displayName}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <span className="text-xs font-semibold text-muted-foreground">
                    {(dealArtist.firstName?.charAt(0) || '') + (dealArtist.lastName?.charAt(0) || '') || dealArtist.displayName.charAt(0)}
                  </span>
                </div>
              )}
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {dealArtist.firstName && dealArtist.lastName 
                    ? `${dealArtist.firstName} ${dealArtist.lastName.charAt(0)}.`
                    : dealArtist.displayName
                  }
                </p>
                {dealArtist.location && (
                  <p className="text-xs text-muted-foreground">
                    {dealArtist.location.city}, {dealArtist.location.country}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="pb-6">
            <div className="text-foreground leading-relaxed whitespace-pre-wrap">
              {deal.dealDescription}
            </div>
          </div>

          {/* Location */}
          {(deal.address || deal.city || deal.country) && (
            <div className="flex items-center gap-2 pb-6 text-muted-foreground">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">
                {deal.address}{deal.address && deal.city ? ', ' : ''}{deal.city}{deal.city && deal.country ? ', ' : ''}{deal.country}
              </span>
            </div>
          )}

          {/* Valid Period - moved before flashes */}
          {(validFromDate || validToDate) && (
            <div className="pb-2">
              <p className="text-muted-foreground text-sm">
                {validFromDate && validToDate 
                  ? `Valid from ${formatDate(validFromDate)} to ${formatDate(validToDate)}`
                  : validFromDate 
                  ? `Valid from ${formatDate(validFromDate)}`
                  : validToDate 
                  ? `Valid until ${formatDate(validToDate)}`
                  : 'Valid for a limited time'
                }
              </p>
            </div>
          )}

          {/* Flashes Grid */}
          {dealFlashes.length > 0 && (
            <BaseMasonryGrid
              items={dealFlashes}
              loading={false}
              loadingMore={false}
              hasMore={false}
              error={null}
              loadMoreItems={async () => {}}
              renderItem={(item) => (
                <FlashCard 
                  item={item} 
                  artist={flashArtists.get(item.artistId)}
                  searchScore={undefined}
                  editMode={false}
                  onDelete={undefined}
                  onMetadataChange={undefined}
                  onMetadataSave={undefined}
                  showCountryFlag={false}
                  onRefreshFlashes={undefined}
                />
              )}
              artists={[]}
              savesLoading={false}
              initialLoadComplete={true}
              editMode={false}
              onDelete={undefined}
            />
          )}

          {dealFlashes.length === 0 && !flashesLoading && (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">No flashes in this deal</p>
            </div>
          )}
        </div>
      </div>

      {/* Banner Upload Options Modal */}
      {showBannerUploadOptions && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto flex items-center justify-center p-6 backdrop-blur-sm" onClick={() => setShowBannerUploadOptions(false)}>
          <div 
            className="bg-background rounded-lg p-8 max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-foreground mb-6 text-center">Upload Banner</h2>
            <BannerUploadOptions 
              isLoading={uploadingBanner}
              bannerInputRef={bannerInputRef}
            />
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={handleBannerImageSelect}
              disabled={uploadingBanner}
            />
            <div className="mt-6 flex justify-center">
              <Button
                variant="outline"
                onClick={() => setShowBannerUploadOptions(false)}
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

